import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doGET } from '../util/HttpUtil';

export const DOC_PHASE = { START: 'START', FINAL: 'FINAL' };
export const DOC_STATUS = { UPLOADED: 'uploaded', PENDING: 'pending' };

export const PARSE_STATE = { READ: 'read', READING: 'reading', UNREADABLE: 'unreadable' };

const REQUIRED_DOCUMENTS_API = '/api/application/document/required';

export const splitByPhase = (documents = []) => ({
    start: documents.filter((row) => row?.phase !== DOC_PHASE.FINAL),
    final: documents.filter((row) => row?.phase === DOC_PHASE.FINAL),
});

const individualName = (individual) => {
    if (!individual) return null;
    const name = [individual.firstName, individual.middleName, individual.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
    return name || individual.email || null;
};

const groupByOwner = (rows, individuals) => {
    const business = [];
    const owners = new Map();

    rows.forEach((row) => {
        if (!row?.documentType) return;
        if (row.forBusiness || !row.ownerId) {
            business.push(row);
            return;
        }
        if (!owners.has(row.ownerId)) owners.set(row.ownerId, []);
        owners.get(row.ownerId).push(row);
    });

    const byId = new Map((individuals || []).filter(Boolean).map((i) => [i._id, i]));

    return {
        business,
        individuals: Array.from(owners.entries()).map(([ownerId, documents]) => ({
            ownerId,
            documents,
            displayName: individualName(byId.get(ownerId)) || 'This person',
        })),
    };
};

const buildQuery = ({ phase, individualId }) => {
    const params = new URLSearchParams();
    if (individualId) {
        params.set('individual_id', individualId);
    } else if (phase) {
        params.set('phase', String(phase).toLowerCase());
    }
    const query = params.toString();
    return query ? `?${query}` : '';
};

const useRequiredDocuments = (formId, {
    individuals,
    enabled = true,
    phase,
    individualId,
    ownersKey = '',
} = {}) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loadedOnce, setLoadedOnce] = useState(false);
    const requestIdRef = useRef(0);

    const fetchRequirements = useCallback(async () => {
        if (!formId || !enabled) return;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const response = await doGET(
                `${REQUIRED_DOCUMENTS_API}/${formId}${buildQuery({ phase, individualId })}`,
            );
            if (requestId !== requestIdRef.current) return;
            if (response?.status === 200) {
                setRows(Array.isArray(response?.data?.documents) ? response.data.documents : []);
                setError(null);
            } else {
                setError(new Error('Failed to load document requirements.'));
            }
        } catch (e) {
            if (requestId !== requestIdRef.current) return;
            setError(e);
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false);
                setLoadedOnce(true);
            }
        }
    }, [formId, enabled, phase, individualId, ownersKey]);

    useEffect(() => {
        fetchRequirements();
    }, [fetchRequirements]);

    const grouped = useMemo(() => groupByOwner(rows, individuals), [rows, individuals]);

    const startRequirements = useMemo(
        () => grouped.business.filter((row) => row.phase === DOC_PHASE.START),
        [grouped.business],
    );

    const missingStart = useMemo(
        () => startRequirements.filter((row) => row.status !== DOC_STATUS.UPLOADED),
        [startRequirements],
    );

    const unsettledStart = useMemo(
        () => startRequirements.filter((row) => (
            row.parseState !== PARSE_STATE.READ && row.parseState !== PARSE_STATE.UNREADABLE
        )),
        [startRequirements],
    );

    return {
        rows,
        business: grouped.business,
        individuals: grouped.individuals,
        startRequirements,
        missingStart,
        hasStartRequirements: startRequirements.length > 0,
        allStartUploaded: missingStart.length === 0,
        unsettledStart,
        allStartRead: unsettledStart.length === 0,
        loading,
        loadedOnce,
        error,
        refetch: fetchRequirements,
    };
};

export default useRequiredDocuments;

